import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { EMPTY_FORM, PLANT_TYPES } from '../../utils/Chloromance/constants';
import { PlantCatalogueType, PlantForm, PlantSample } from '../../utils/Chloromance/types';
import { formatMonthList } from '../../utils/Chloromance/utils/catalogue';
import { formatLocationOption } from '../../utils/Chloromance/utils/plants';


type PlantFormDrawerProps = {
  form: PlantForm;
  zones: string[];
  samples: PlantSample[];
  onChange: (form: PlantForm) => void;
  onSelectSample: (sampleId: string) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  isEditing?: boolean;
};

type FormMode = 'catalogue' | 'custom';
type CatalogueFilter = 'All' | PlantCatalogueType;

export function PlantFormDrawer({ form, zones, samples, onChange, onSelectSample, onClose, onSubmit, isEditing = false }: PlantFormDrawerProps) {
  const [mode, setMode] = useState<FormMode>(() => isEditing ? 'custom' : 'catalogue');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<CatalogueFilter>('All');
  const categories = useMemo(
    () => Array.from(new Set(samples.map((sample) => sample.type))).sort(),
    [samples]
  );
  const selectedSample = samples.find((sample) => sample.id === form.sample_id);
  const filteredSamples = useMemo(() => {
    const search = query.trim().toLowerCase();
    return samples.filter((sample) => {
      const matchesCategory = category === 'All' || sample.type === category;
      const matchesSearch = !search || `${sample.name} ${sample.variety} ${sample.notes}`.toLowerCase().includes(search);
      return matchesCategory && matchesSearch;
    });
  }, [samples, query, category]);
  const selectableSamples = selectedSample && !filteredSamples.some((sample) => sample.id === selectedSample.id)
    ? [selectedSample, ...filteredSamples]
    : filteredSamples;

  const showEditableCareProfile = mode === 'custom' || Boolean(selectedSample);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const changeMode = (nextMode: FormMode) => {
    setMode(nextMode);
    if (nextMode === 'custom' && !isEditing) onChange(EMPTY_FORM);
  };

  return (
    <div className="form-backdrop" role="presentation" onMouseDown={onClose}>
      <aside className="plant-drawer plant-drawer-catalogue" aria-label="Add a plant" onMouseDown={(event) => event.stopPropagation()}>
        <div className="drawer-header">
          <div><p>{isEditing ? 'Edit plant' : 'Add plant'}</p><h2>{isEditing ? 'Edit plant details' : 'New hydration profile'}</h2></div>
          <button type="button" onClick={onClose} aria-label={isEditing ? 'Close edit plant form' : 'Close add plant form'}>×</button>
        </div>

        <div className="plant-add-mode" role="tablist" aria-label="Choose plant entry method">
          <button type="button" className={mode === 'catalogue' ? 'is-active' : ''} onClick={() => changeMode('catalogue')}>
            Choose from {samples.length} plants
          </button>
          <button type="button" className={mode === 'custom' ? 'is-active' : ''} onClick={() => changeMode('custom')}>
            Add custom plant
          </button>
        </div>

        <form onSubmit={onSubmit}>
          {mode === 'catalogue' && (
            <section className="plant-catalogue-picker" aria-label="Plant catalogue">
              <div className="plant-catalogue-filters">
                <label>
                  Search catalogue
                  <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search tomato, fern, orchid..." />
                </label>
                <label>
                  Category
                  <select value={category} onChange={(event) => setCategory(event.target.value as CatalogueFilter)}>
                    <option value="All">All plants</option>
                    {categories.map((catalogueType) => (
                      <option key={catalogueType} value={catalogueType}>{catalogueType}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label>
                Select a plant ({filteredSamples.length} matching)
                <select value={form.sample_id} onChange={(event) => onSelectSample(event.target.value)} required={mode === 'catalogue'}>
                  <option value="">Select a plant to auto-fill care settings</option>
                  {selectableSamples.map((sample) => (
                    <option key={sample.id} value={sample.id}>{sample.type} · {sample.name} — {sample.variety}</option>
                  ))}
                </select>
              </label>

              {selectedSample && (
                <article className="plant-sample-preview" aria-live="polite">
                  <div><span className="catalogue-chip">{selectedSample.type}</span><strong>{selectedSample.name}</strong></div>
                  <p>{selectedSample.variety}</p>
                  <dl>
                    <div><dt>Water every</dt><dd>{selectedSample.wateringFrequencyDays} days</dd></div>
                    <div><dt>Suggested position</dt><dd>{selectedSample.suggestedLocation}</dd></div>
                    <div><dt>Flowers / fruit</dt><dd>{formatMonthList(selectedSample.fruitOrFlowerMonths)}</dd></div>
                    <div><dt>Harvest</dt><dd>{formatMonthList(selectedSample.bestHarvestMonths)}</dd></div>
                  </dl>
                </article>
              )}
            </section>
          )}

          {showEditableCareProfile && (
            <fieldset className="care-profile-fields">
              <legend>{mode === 'catalogue' ? 'Review and personalise' : 'Custom plant details'}</legend>
              <label>Plant name<input value={form.name} onChange={(event) => onChange({ ...form, name: event.target.value })} placeholder="Roma Tomato" required /></label>
              <label>Variety / species<input value={form.variety} onChange={(event) => onChange({ ...form, variety: event.target.value })} placeholder="Solanum lycopersicum" required /></label>
              <label>
                Plant type
                <select value={form.plant_type} onChange={(event) => onChange({ ...form, plant_type: event.target.value })}>
                  {PLANT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </label>
              <label>Number of plants<input min="1" type="number" value={form.plant_count} onChange={(event) => onChange({ ...form, plant_count: event.target.value })} required /></label>
              <label>
                Where will you keep it?
                <select value={form.chloromance_zone} onChange={(event) => onChange({ ...form, chloromance_zone: event.target.value })}>
                  {zones.map((zone) => <option key={zone} value={zone}>{formatLocationOption(zone)}</option>)}
                </select>
              </label>
              <label>Watering frequency<input min="1" type="number" value={form.watering_frequency_days} onChange={(event) => onChange({ ...form, watering_frequency_days: event.target.value })} required /></label>
              {form.suggested_location && <p className="suggested-location"><strong>Catalogue suggestion:</strong> {form.suggested_location}</p>}
              <label>Optional note<textarea value={form.note} onChange={(event) => onChange({ ...form, note: event.target.value })} placeholder="Humidity, pests, pruning, feeding..." /></label>
              <button className="primary-action" type="submit">{isEditing ? 'Save Changes' : 'Save Plant'}</button>
            </fieldset>
          )}
        </form>
      </aside>
    </div>
  );
}
